import React from "react";
import styled from "styled-components";

import { deserialize } from "@examspark/api";
import { gradeLevelMeta } from "@examspark/common-meta";
import { HSpace, VSpace } from "@examspark/common-ui";
import {
  LinkCard,
  CardLink,
  CardAction,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { UserClassroom } from "@examspark/types";

import googleClassroomIcon from "../../images/google-classroom-indicator.png";
import SubjectIcon from "../common/SubjectIcon";

const StyledSectionInfo = styled(CardLink)`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

export const StyledSectionBox = styled(LinkCard)`
  min-height: clamp(14rem, 25vh, 20rem);

  // change the highlight color
  &:hover,
  &:focus-within {
    --input-glow: transparent;
    border: solid 2px ${colorFromTheme("primary")};
  }
`;

const StyledSectionHeader = styled.div`
  display: flex;
  gap: 2rem;

  justify-content: space-between;

  img {
    transition: all 0.2s;
    opacity: 0.3;
    filter: grayscale(1);
  }
`;

export const NewSectionBox = styled(StyledSectionBox).attrs({
  as: "button",
})`
  align-items: center;
  justify-content: center;
  border: dashed 2px ${colorFromTheme("surface5")};
  transition: border 0.2s;
  cursor: pointer;
  color: #006699;
  font-family: inherit;
  font-size: 1.4rem;
  gap: 1rem;

  background-color: white;
  outline: transparent;

  svg {
    fill: #006699;
  }

  &:hover,
  &:focus-visible {
    border: dashed 2px ${colorFromTheme("primary")};
  }
`;

const StyledSectionInfoProgress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  width: 100%;
`;

const ClassProgressProse = styled.div`
  align-self: flex-end;
  /* color: ${colorFromTheme("primary", "textColor")}; */
  color: #777;
  font-size: 1.15rem;
  display: flex;
  gap: 0.7rem;
`;

const ClassProgressBar = styled.div`
  width: 100%;
  height: 1rem;
  display: flex;
  gap: 1px;
`;
const StyledProgressBarItem = styled.div<{ progress: number }>`
  flex-basis: 1px;
  flex-grow: ${(props) => props.progress};
  --radius: 0.5rem;

  &:first-child {
    background-color: ${colorFromTheme("primary")};
    border-radius: var(--radius) 0 0 var(--radius);
  }
  &:last-child {
    background-color: #e0e0e0;
    /* background-color: ${colorFromTheme("secondary")}; */
    border-radius: 0 var(--radius) var(--radius) 0;
  }
  // aka, only child
  &:first-child:last-child {
    border-radius: var(--radius);
  }
`;

const StyledGrade = styled.div`
  font-size: 1.2rem;
`;

const StyledTitle = styled.div`
  font-size: 1.5rem;
`;
const ClassroomMeta = styled(HSpace)`
  gap: 0.3rem 0.6rem;
  color: #777;
  font-size: 1.1rem;
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;
const ClassroomGoogleIcon = styled.div`
  img {
    filter: grayscale(1);
    opacity: 0.7;
    display: flex;
  }
`;
const StyledSection = styled.div`
  font-size: 1.3rem;
  color: ${colorFromTheme("secondary", "textColor")};
`;

const ProseCourseTitle = styled.div``;
const ProseProgress = styled.div``;

const MetaMain = styled.div`
  flex: 1;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;
const MetaUpdates = styled.div``;

type SectionBoxProps = {
  userClassroom: UserClassroom;
  to: string;
};
const SectionBox = ({ userClassroom, to }: SectionBoxProps) => {
  const classroom = deserialize.parseClassroom(userClassroom.classroom);
  const unitIndex = classroom?.course?.unitIds?.findIndex(
    (cu) => cu.id === classroom.unitId,
  );
  const unitCount = classroom?.course?.unitIds?.length ?? 0;

  return (
    <StyledSectionBox>
      <StyledSectionHeader>
        <StyledSectionInfo to={to}>
          <StyledGrade>
            {/* this is not right */}
            {gradeLevelMeta[
              userClassroom.classroom?.grade?.[0] as keyof typeof gradeLevelMeta
            ]?.label ?? "Grade Not Set"}
          </StyledGrade>
          <StyledTitle>
            {userClassroom.classroom?.name ?? "Name Missing"}
          </StyledTitle>
          <StyledSection>
            {userClassroom.classroom?.sectionNumber ?? "Section ABC"}
          </StyledSection>
          <StyledSection>
            {userClassroom.classroom?.users?.map((u) => {
              return (
                <div key={u.user?.id}>
                  {u.user?.firstName + " " + u.user?.lastName}
                </div>
              );
            })}
          </StyledSection>
        </StyledSectionInfo>
        <SubjectIcon value={userClassroom.classroom?.subject?.[0]} title="" />
      </StyledSectionHeader>

      <VSpace style={{ gap: "1.5rem" }}>
        {userClassroom.classroom?.course && (
          <StyledSectionInfoProgress>
            <ClassProgressProse>
              <ProseCourseTitle>
                {userClassroom?.classroom?.course?.name ?? "**Missing Course**"}
              </ProseCourseTitle>
              <ProseProgress>
                ({(unitIndex ?? -1) + 1}/{unitCount})
              </ProseProgress>
            </ClassProgressProse>
            <ClassProgressBar>
              {unitIndex === -1 ? null : (
                <StyledProgressBarItem progress={(unitIndex ?? -1) + 1} />
              )}
              <StyledProgressBarItem progress={unitCount ?? 1} />
            </ClassProgressBar>
          </StyledSectionInfoProgress>
        )}

        <ClassroomMeta>
          <MetaMain>
            {classroom?.data?.googleClassroom && (
              // wrap in a CardAction so that title shows on hover
              <CardAction>
                <ClassroomGoogleIcon>
                  <img
                    height="16"
                    src={googleClassroomIcon}
                    alt="Imported from Google"
                    title="Imported from Google"
                  />
                </ClassroomGoogleIcon>
              </CardAction>
            )}
            {/* <ClassEnrollment>
              {userClassroom.classroom?.studentCount ?? "[Unknown]"} Students
              Enrolled
            </ClassEnrollment> */}
          </MetaMain>
          <MetaUpdates>
            {userClassroom.classroom?.createdAt && (
              <div>
                {`Created: ${new Intl.DateTimeFormat().format(
                  new Date(userClassroom.classroom.createdAt),
                )}`}
              </div>
            )}
          </MetaUpdates>
        </ClassroomMeta>
      </VSpace>
    </StyledSectionBox>
  );
};

export default SectionBox;
